import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ITimer, ITimersApi } from "../types/timer";

const Ctx = createContext<ITimersApi | null>(null);
const LS_KEY = "coocoo.timers.v1";

function uid() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return (crypto as any).randomUUID();
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const minutesToMs = (m: number) => Math.max(0, Math.floor(m)) * 60_000;
const secondsToMs = (s: number) => Math.max(0, Math.min(59, Math.floor(s))) * 1000;

const TimersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [timers, setTimers] = useState<ITimer[]>([]);
    const [minimized, setMinimized] = useState(true);

    // rAF bookkeeping
    const rafRef = useRef<number | null>(null);
    const lastTickRef = useRef<number | null>(null);

    // Load persisted state
    useEffect(() => {
        try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw) {
                const parsed = JSON.parse(raw) as { timers?: ITimer[]; minimized?: boolean };
                if (Array.isArray(parsed.timers)) setTimers(parsed.timers);
                if (typeof parsed.minimized === "boolean") setMinimized(parsed.minimized);
            }
        } catch {
            /* ignore */
        }
    }, []);

    // Persist on change
    useEffect(() => {
        localStorage.setItem(LS_KEY, JSON.stringify({ timers, minimized }));
    }, [timers, minimized]);

    // Derived: how many are running?
    const runningCount = useMemo(() => timers.reduce((n, t) => n + (t.running ? 1 : 0), 0), [timers]);

    // Start/stop ticker depending on running timers
    useEffect(() => {
        if (runningCount === 0) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            lastTickRef.current = null;
            rafRef.current = null;
            return;
        }

        const tick = (now: number) => {
            if (lastTickRef.current == null) lastTickRef.current = now;
            const dt = now - lastTickRef.current;
            lastTickRef.current = now;

            setTimers((prev) =>
                prev.map((t) => {
                    if (!t.running) return t;
                    const nextRemaining = Math.max(0, t.remainingMs - dt);
                    const hitZero = nextRemaining === 0 && t.remainingMs !== 0;
                    return {
                        ...t,
                        remainingMs: nextRemaining,
                        running: hitZero ? false : t.running, // auto-pause on finish
                    };
                })
            );

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [runningCount]);

    // Helpers to read/modify a timer
    const setTimer = (id: string, updater: (t: ITimer) => ITimer) => {
        setTimers((prev) => prev.map((t) => (t.id === id ? updater(t) : t)));
    };

    const addTimer: ITimersApi["addTimer"] = useCallback(
        (opts) => {
            const index = timers.length + 1;
            const minutes = opts?.minutes ?? 5;
            const seconds = opts?.seconds ?? 0;
            const total = minutesToMs(minutes) + secondsToMs(seconds);
            const label = (opts?.label ?? "").trim() || `Timer ${index}`;
            const t: ITimer = {
                id: uid(),
                label,
                totalMs: total,
                remainingMs: total,
                running: false,
                createdAt: Date.now(),
            };
            setTimers((prev) => [...prev, t]);
            setMinimized(false);
        },
        [timers]
    );

    const removeTimer: ITimersApi["removeTimer"] = (id) => {
        setTimers((prev) => prev.filter((t) => t.id !== id));
    };

    const renameTimer: ITimersApi["renameTimer"] = useCallback((id, label) => {
        setTimer(id, (t) => ({ ...t, label }));
    }, []);

    const setTimerMinutes: ITimersApi["setTimerMinutes"] = useCallback((id, minutes) => {
        setTimer(id, (t) => {
            const secondsPart = Math.floor((t.totalMs % 60_000) / 1000);
            const newTotal = minutesToMs(minutes) + secondsToMs(secondsPart);
            return { ...t, totalMs: newTotal, remainingMs: newTotal }; //Math.min(t.remainingMs, newTotal)
        });
    }, []);

    const setTimerSeconds: ITimersApi["setTimerSeconds"] = useCallback((id, seconds) => {
        setTimer(id, (t) => {
            const minutesPart = Math.floor(t.totalMs / 60_000);
            const newTotal = minutesToMs(minutesPart) + secondsToMs(seconds);
            return { ...t, totalMs: newTotal, remainingMs: newTotal }; //Math.min(t.remainingMs, newTotal)
        });
    }, []);

    const start: ITimersApi["start"] = useCallback((id) => setTimer(id, (t) => ({ ...t, running: t.remainingMs > 0 })), []);
    const pause: ITimersApi["pause"] = useCallback((id) => setTimer(id, (t) => ({ ...t, running: false })), []);
    const reset: ITimersApi["reset"] = useCallback((id) => setTimer(id, (t) => ({ ...t, remainingMs: t.totalMs, running: false })), []);

    const startAll = () => setTimers((prev) => prev.map((t) => ({ ...t, running: t.remainingMs > 0 })));
    const pauseAll = () => setTimers((prev) => prev.map((t) => ({ ...t, running: false })));
    const resetAll = () => setTimers((prev) => prev.map((t) => ({ ...t, remainingMs: t.totalMs, running: false })));

    const toggleMinimized = () => setMinimized((m) => !m);

    const api: ITimersApi = useMemo(
        () => ({
            timers,
            minimized,
            addTimer,
            removeTimer,
            renameTimer,
            setTimerMinutes,
            setTimerSeconds,
            start,
            pause,
            reset,
            startAll,
            pauseAll,
            resetAll,
            toggleMinimized,
        }),
        [timers, minimized, addTimer, renameTimer, setTimerMinutes, setTimerSeconds, start, pause, reset]
    );

    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
};

export const useTimers = () => {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error("useTimers must be used within TimersProvider");
    return ctx;
};

export default TimersProvider;
